import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, Phone, Clock, ArrowRight, AlertCircle, UtensilsCrossed, PawPrint, Wheat } from "lucide-react";
import api, { API } from "../lib/api";
import useDescTranslate from "../lib/useDescTranslate";
import LanguageSelector from "../components/LanguageSelector";
import { MenuExplorer } from "./RestaurantHome";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1766832255363-c9f060ade8b0?crop=entropy&cs=srgb&fm=jpg&q=85";

/**
 * Private Mirror page — accessible via /m/{token}.
 * Identical to RestaurantHome but:
 *   - Uses the unguessable token instead of slug
 *   - Hides every link to other restaurants / Renova directory
 *   - Shows the restaurant's logo if uploaded
 *   - Booking CTA still works via the regular /r/{slug}/book route
 */
export default function PrivateMirror() {
    const { token } = useParams();
    const { t } = useTranslation();
    const [restaurant, setRestaurant] = useState(null);
    const [menu, setMenu] = useState([]);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get(`/m/${token}`)
            .then((r) => setRestaurant(r.data))
            .catch(() => setError(true));
    }, [token]);

    useEffect(() => {
        if (!restaurant?.slug) return;
        api.get(`/r/${restaurant.slug}/menu`).then((r) => setMenu(r.data)).catch(() => {});
    }, [restaurant?.slug]);

    // Translate the restaurant's description dynamically when the visitor
    // selects a non-Italian language from the language selector.
    const { tx: txDesc } = useDescTranslate(restaurant?.description ? [restaurant.description] : []);

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-ink px-6 pt-32" data-testid="mirror-not-found">
                <div className="text-center max-w-md">
                    <AlertCircle size={48} className="text-gold mx-auto mb-4" strokeWidth={1} />
                    <h1 className="font-serif text-3xl text-white">Pagina non disponibile</h1>
                    <p className="text-gray-400 mt-3">Il link che stai utilizzando non è più valido.</p>
                </div>
            </div>
        );
    }
    if (!restaurant) {
        return <div className="min-h-screen flex items-center justify-center bg-ink"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;
    }

    const gallery = restaurant.gallery || [];
    const heroImg = gallery[0] ? `${API}/files/${gallery[0]}` : FALLBACK_IMG;
    const logoUrl = restaurant.logo_path ? `${API}/files/${restaurant.logo_path}` : null;
    const bookingHref = `/r/${restaurant.slug}/book?from=mirror&t=${token}`;

    return (
        <div className="min-h-screen bg-ink" data-testid="private-mirror-page">
            {/* Custom mini-header with the restaurant's own logo */}
            <header className="absolute top-0 inset-x-0 z-30 px-6 md:px-10 py-5 flex items-center justify-between" data-testid="mirror-header">
                <div className="flex items-center gap-3">
                    {logoUrl ? (
                        <img
                            src={logoUrl}
                            alt={`${restaurant.name} logo`}
                            className={`object-contain rounded-sm bg-white/5 p-1.5 ${
                                restaurant.white_label ? "h-20 w-20 md:h-24 md:w-24" : "h-14 w-14 md:h-16 md:w-16"
                            }`}
                            data-testid="mirror-logo"
                        />
                    ) : (
                        <div className={`flex items-center justify-center bg-gold/10 border border-gold/30 rounded-sm ${
                            restaurant.white_label ? "h-20 w-20 md:h-24 md:w-24" : "h-14 w-14 md:h-16 md:w-16"
                        }`}>
                            <span className={`font-serif text-gold ${restaurant.white_label ? "text-3xl md:text-4xl" : "text-2xl"}`}>{restaurant.name?.[0]}</span>
                        </div>
                    )}
                    <div className={`text-white font-serif hidden sm:block ${restaurant.white_label ? "text-xl md:text-2xl" : "text-base md:text-lg"}`}>{restaurant.name}</div>
                </div>
                <div className="flex items-center gap-3">
                    <LanguageSelector />
                    <a href={bookingHref}
                        className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-5 py-2.5 rounded-sm hover:bg-gold-light text-xs tracking-widest uppercase"
                        data-testid="mirror-book-btn-top">
                        {t("common.book")} <ArrowRight size={13} />
                    </a>
                </div>
            </header>

            {/* HERO */}
            <section className="relative min-h-screen flex items-center overflow-hidden grain">
                <div className="absolute inset-0 z-0">
                    <img src={heroImg} alt={restaurant.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/60 to-black/95" />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-20 w-full">
                    <div className="max-w-3xl animate-fade-up">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="h-px w-12 bg-gold" />
                            <span className="text-gold text-xs tracking-[0.4em] uppercase">{t("restaurant_home.welcome")}</span>
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light text-white leading-[1.05] tracking-tight" data-testid="mirror-name">
                            {restaurant.name}
                        </h1>
                        {restaurant.gluten_free && (
                            <div className="mt-4 inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 px-3 py-1.5 rounded-sm text-xs font-semibold tracking-widest uppercase" data-testid="mirror-gluten-free-badge">
                                <Wheat size={13} strokeWidth={1.8} /> {t("badges.gluten_free")}
                            </div>
                        )}
                        {restaurant.description && (
                            <p className="mt-8 text-lg text-gray-300 max-w-xl font-light leading-relaxed" data-testid="mirror-description">
                                {txDesc(restaurant.description)}
                            </p>
                        )}
                        <div className="mt-10">
                            <a href={bookingHref}
                                className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-8 py-4 rounded-sm hover:bg-gold-light tracking-wide"
                                data-testid="mirror-book-btn-hero">
                                {t("common.book_table")} <ArrowRight size={16} />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* INFO */}
            <section id="info" className="px-6 md:px-10 py-20 max-w-5xl mx-auto" data-testid="mirror-info">
                <div className="grid sm:grid-cols-2 gap-8">
                    {restaurant.address && <InfoItem icon={MapPin} label={t("restaurant_home.address_label")} text={restaurant.address} />}
                    {restaurant.phone && <InfoItem icon={Phone} label={t("restaurant_home.phone")} text={restaurant.phone} />}
                    {restaurant.opening_hours && <InfoItem icon={Clock} label={t("restaurant_home.opening_hours")} text={restaurant.opening_hours} />}
                    <InfoItem
                        icon={PawPrint}
                        label={restaurant.pets_allowed ? t("restaurant_home.pets_accepted") : t("restaurant_home.pets_not_accepted")}
                        text=""
                        accent={restaurant.pets_allowed ? "emerald" : "rose"}
                    />
                </div>
            </section>

            {/* MENU PREVIEW */}
            {menu.length > 0 && (
                <MenuExplorer menu={menu} bookHref={`/r/${restaurant.slug}/book?from=mirror`} />
            )}

            {/* FINAL CTA */}
            <section className="px-6 md:px-10 py-20 text-center">
                <div className="flex justify-center mb-4">
                    <Calendar size={28} className="text-gold" strokeWidth={1.5} />
                </div>
                <h2 className="font-serif text-3xl text-white mb-3">{t("restaurant_home.table_waiting_1")} {t("restaurant_home.table_waiting_2")}</h2>
                <p className="text-gray-400 max-w-md mx-auto mb-6">{t("restaurant_home.table_waiting_subtitle")}</p>
                <a href={bookingHref}
                    className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-8 py-4 rounded-sm hover:bg-gold-light tracking-wide"
                    data-testid="mirror-book-btn-bottom">
                    {t("restaurant_home.book_now")} <ArrowRight size={16} />
                </a>
            </section>

            {/* Mirror footer — discreet attribution */}
            <footer className="text-center text-[11px] text-gray-700 py-8 border-t border-white/5" data-testid="mirror-footer">
                <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
                    <span>© {new Date().getFullYear()} {restaurant.name}</span>
                    <span className="opacity-60">·</span>
                    <Link to="/privacy" className="hover:text-gold">Privacy</Link>
                    <span className="opacity-60">·</span>
                    <Link to="/cookie-policy" className="hover:text-gold">Cookie</Link>
                </div>
                {!restaurant.white_label && (
                    <div className="opacity-50 mt-2" data-testid="mirror-powered-by">Powered by Prenova by Renova360</div>
                )}
            </footer>
        </div>
    );
}

function InfoItem({ icon: Icon, label, text, accent }) {
    const accentMap = {
        emerald: { icon: "text-emerald-300", text: "text-emerald-300" },
        rose: { icon: "text-rose-400", text: "text-rose-400" },
    };
    const tone = accentMap[accent] || { icon: "text-gold", text: "text-white" };
    return (
        <div className="flex items-start gap-4">
            <Icon size={20} className={`${tone.icon} mt-1`} strokeWidth={1.5} />
            <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-gray-500">{label}</div>
                <div className={`${tone.text} mt-1`}>{text}</div>
            </div>
        </div>
    );
}
